import { useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { csv } from 'd3-fetch'
import { scaleLinear } from 'd3-scale'
import { Theme } from '../../styles/settings/theme'
import { MediaQueries as Mq } from '../../styles/settings/theme'
import { Flex, Content } from '../atoms/Basics'
import ChartTitle from '../atoms/ChartTitle'
import ChartContainer from '../atoms/ChartContainer'
import Subtitle from '../atoms/Subtitle'
import Note from '../atoms/Note'

const RowFlex = styled(Flex)`
  margin-bottom: 0.5rem;
`
const Label = styled.div`
  color: ${(props) =>
    props.hover ? props.theme.colors.black : props.theme.colors.gray};
  margin: 0 1rem;
  //min-width: 9.6rem;
  width: 10.25rem;
  font-size: ${(props) => props.theme.fontSizes[0]};
  font-weight: 600;
  ${Mq.md} {
    font-size: 12px;
    width: 8rem;
  }
  ${Mq.sm} {
    font-size: 11px;
    width: 7rem;
  }
  ${Mq.xs} {
    font-size: 10px;
    width: 6.5rem;
    margin: 0 0.25rem 0 1rem;
  }
`
const RespondentsBar = styled.div`
  flex: 3;
  height: 1.5rem;
  background-color: ${(props) =>
    props.hover ? 'rgba(40, 135, 190, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
  position: relative;
`
const NumRespondents = styled.div`
  position: absolute;
  top: 0;
  background-color: ${(props) =>
    props.hover ? props.theme.colors.blue : props.theme.colors.gray};
  height: 1.5rem;
  width: ${(props) => props.width}%;
`
const NumLabel = styled.p`
  position: absolute;
  top: 0;
  left: ${(props) => props.width}%;
  font-weight: 700;
  color: ${(props) =>
    props.type === 'resp'
      ? props.theme.colors.blue
      : props.theme.colors.orange};
  display: ${(props) => (props.hover ? 'block' : 'none')};
  ${Mq.xs} {
    font-size: 10px;
    margin-top: .25rem;
`
const ProficiencyBar = styled.div`
  flex: 1;
  height: 1.5rem;
  background-color: ${(props) =>
    props.hover ? 'rgba(236, 120, 36, 0.08)' : 'rgba(0, 0, 0, 0.08)'};
  margin: 0 1rem;
  position: relative;
`
const AvgProficiency = styled.div`
  position: absolute;
  top: 0;
  background-color: ${(props) =>
    props.hover ? props.theme.colors.orange : props.theme.colors.lightGray};
  height: 1.5rem;
  width: ${(props) => props.width}%;
`
const TickContainer = styled.div`
  flex: ${(props) => (props.type === 'resp' ? 3 : 1)};
  margin: 0 ${(props) => (props.type === 'resp' ? 0 : '1rem')};
  position: relative;
  height: 6rem;
`
const Tick = styled.div`
  position: absolute;
  left: ${(props) => props.tick}%;
  bottom: 0;
  // transform: translate(${(props) => props.tick}%, 0);
  color: ${(props) => props.theme.colors.gray};
  height: 0.6rem;
  border-right: 1px solid gray;
`

const TickLabel = styled.div`
  position: absolute;
  left: ${(props) => props.tick}%;
  bottom: 0.75rem;
  font-size: 0.75rem;
  color: ${(props) => props.theme.colors.gray};
  text-align: center;
  ${Mq.sm} {
    font-size: 0.5rem;
  }
`

const TechUse = ({ note }) => {
  const $ = window.WILDLABS_VARS
  const [data, setData] = useState(null)
  const [hoveredIndex, setHoveredIndex] = useState(null)
  useEffect(() => {
    csv('./data/techUse.csv').then((data) => {
      data.sort((a, b) => b.respondents - a.respondents)
      setData(data)
    })
  }, [])
  //find hightest value of respondents and add a little to make room for number label
  const respondentsScale = data ? +data[0].respondents + 10 : null
  const proficiencyScale = 5

  const handleMouseEnter = (index) => setHoveredIndex(index)
  const handleMouseLeave = () => setHoveredIndex(null)

  const x1 = scaleLinear([0, respondentsScale], [0, respondentsScale])
  const numTicks1 = Math.floor(respondentsScale / 20)
  const xTicks1 = x1.ticks(numTicks1)

  const x2 = scaleLinear([0, proficiencyScale], [0, proficiencyScale])
  const xTicks2 = x2.ticks(5)

  const center = (tick) => {
    return tick < 10 ? 0.5 : tick < 100 ? 0.9 : 1.75
  }

  const isSmall = window.innerWidth < 500
  return (
    <ChartContainer>
      <ChartTitle text={$.tech_titles.chartTitle} />

      <Content flexDirection="column">
        <RowFlex>
          <Label></Label>
          <TickContainer type="resp">
            <Flex justifyContent="center">
              <Subtitle
                text={$.tech_titles.subtitleA}
                size={Theme.fontSizes[3]}
                color={Theme.colors.gray}
              ></Subtitle>
            </Flex>
            <Flex>
              {xTicks1.map((tick, i) => {
                return (
                  <div key={tick * i}>
                    <TickLabel
                      key={`i + ${tick}`}
                      tick={(tick / respondentsScale) * 100 - center(tick)}
                    >
                      {tick}
                    </TickLabel>
                    <Tick key={tick} tick={(tick / respondentsScale) * 100} />
                  </div>
                )
              })}
            </Flex>
          </TickContainer>
          <TickContainer type="prof">
            <Flex justifyContent="center">
              <Subtitle
                text={
                  isSmall ? $.tech_titles.smSubtitleB : $.tech_titles.subtitleB
                }
                size={Theme.fontSizes[3]}
                color={Theme.colors.lightGray}
              ></Subtitle>
            </Flex>
            <Flex>
              {xTicks2.map((tick, i) => {
                return (
                  <div key={tick * i}>
                    <TickLabel
                      key={tick * i}
                      tick={(tick / proficiencyScale) * 100 - center(tick)}
                    >
                      {tick}
                    </TickLabel>
                    <Tick
                      key={tick + 'a'}
                      tick={(tick / proficiencyScale) * 100}
                    />
                  </div>
                )
              })}
            </Flex>
          </TickContainer>
        </RowFlex>
        {data?.map((row, i) => (
          <RowFlex
            key={row.group}
            onMouseEnter={() => handleMouseEnter(i)}
            onMouseLeave={() => handleMouseLeave()}
            alignItems="center"
          >
            <Label hover={i === hoveredIndex}>{row.group}</Label>
            <RespondentsBar hover={i === hoveredIndex}>
              <NumRespondents
                width={(row.respondents / respondentsScale) * 100}
                hover={i === hoveredIndex}
              ></NumRespondents>
              <NumLabel
                width={(row.respondents / respondentsScale) * 100 + 1}
                hover={i === hoveredIndex}
                type="resp"
              >
                {row.respondents}
              </NumLabel>
            </RespondentsBar>
            <ProficiencyBar hover={i === hoveredIndex}>
              <AvgProficiency
                width={(row.avgProficiency / proficiencyScale) * 100}
                hover={i === hoveredIndex}
              ></AvgProficiency>
              <NumLabel
                width={(row.avgProficiency / proficiencyScale) * 100 + 3}
                hover={i === hoveredIndex}
                type="prof"
              >
                {row.avgProficiency}
              </NumLabel>
            </ProficiencyBar>
          </RowFlex>
        ))}
        <Note note={note} type={'tech'} />
      </Content>
    </ChartContainer>
  )
}

export default TechUse
